import { useSelector } from 'react-redux';
import React, { useState } from 'react';
import moment from 'moment';
import {
  useMutation,
  Query,
  useApolloClient,
  useSubscription,
} from 'react-apollo';
import {
  makeStyles,
  styled,
  useTheme,
  withStyles,
} from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import useSnack from 'hooks/useSnack';
import Loading from 'components/common/Spin';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import {
  GET_UNIVERSITIES,
  INSERT_UNIVERSITY,
  DELETE_UNIVERSITY,
  UPDATE_UNIVERSITY,
} from 'graphql/student/essays';
import { LISTEN_ESSAY_CREDITS } from 'graphql/essays';
import concat from 'lodash/concat';
import Universities from './Universities';
import AddUniversatyModal from './add-uni';
import AddDocumentModal from './add-doc';
import Helmet from 'react-helmet';
import { Link, Switch, Route, Link as NavLink } from 'react-router-dom';
import Box from '@material-ui/core/Box';
import Brainstorm from './Brainstorm';
import ShowLogs from './show-logs';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import PRODUCTS from 'constant/product-types';
import withConfirm from 'hooks/withConfirm';

const NavTab = withStyles((theme) => ({
  root: {
    minWidth: 72,
  },
}))(Tab);

const Footer = styled('div')({
  'marginTop': 60,
  '& h5': {
    marginBottom: 24,
    fontWeight: 500,
  },

});

const PRODUCTS_LOOKUP = PRODUCTS.reduce((memo, i) => {
  memo[i.value] = i.label;
  return memo;
}, {});

const useStyles = makeStyles((theme) => ({
  leftIcon: {
    marginRight: theme.spacing(1)
  },
}));

const Essays = ({
  history,
  location: { pathname },
  student,
  readOnly,
  confirm,
}) => {
  const classes = useStyles();
  const [insertUniversity] = useMutation(INSERT_UNIVERSITY);
  const [updateUniversity] = useMutation(UPDATE_UNIVERSITY);
  const [deleteUniversity] = useMutation(DELETE_UNIVERSITY);
  const { data: creditsResp } = useSubscription(LISTEN_ESSAY_CREDITS, {
    variables: { userId: student.id },
  });
  const user = useSelector((state) => state.user.user);
  const [showUniModal, setUniModal] = useState(false);
  const [showAddDoc, setAddDoc] = useState();
  const [showLogs, setShowLogs] = useState();
  const client = useApolloClient();
  const snack = useSnack();
  const isStudent = user.type === 'student';
  const queryVariables = { usersub: isStudent ? user.sub : student.sub };
  const routePrefix = isStudent ? '' : `/students/${student.id}`;
  const { product_type } = student;

  const onAdd = (values) => {
    const payload = {
      university_name: values.university_name,
      due_date: moment(values.due_date).format('YYYY-MM-DD'),
      usersub: queryVariables.usersub,
      schools: values.schools,
    };

    if (showUniModal.id) {
      return updateUniversity({
        variables: {
          id: showUniModal.id,
          data: payload,
        },
      }).then(() => {
        setUniModal(false);
        snack.success('University updated');
      });
    } else {
      return insertUniversity({
        variables: {
          data: [payload],
        },
        update: (
          cache,
          {
            data: {
              universities: { returning },
            },
          },
        ) => {
          try {
            let { universities } = cache.readQuery({
              query: GET_UNIVERSITIES,
              variables: queryVariables,
            });
            cache.writeQuery({
              query: GET_UNIVERSITIES,
              variables: queryVariables,
              data: {
                universities: concat(returning, universities),
              },
            });
          } catch (e) {
            console.log(e);
          }
        },
      }).then(() => {
        setUniModal(false);
        window.analytics.track('Essay card created', {
          ...payload,
          product: PRODUCTS_LOOKUP[product_type],
        });
        snack.success('University added');
      });
    }
  };

  const onEditUniversity = (uni) => {
    setUniModal(uni);
  };

  const onViewLogs = (uni) => {
    setShowLogs(uni);
  };

  const onAddUniversity = (collegesRemaining) => {
    if (!collegesRemaining) {
      confirm(
        () => {
          history.push('/addons');
        },
        {
          title: 'Not enough College Credits',
          description: `You do not have enough college credits to add a university. Please purchase more credits.`,
          confirmationText: 'Purchase Credits',
          cancellationText: 'Cancel',
        },
      );
    } else {
      setUniModal(true);
    }
  };

  const onDeleteUniversity = (uni) => {
    return deleteUniversity({
      variables: {
        id: uni.id,
      },
      update: (cache) => {
        let { universities } = cache.readQuery({
          query: GET_UNIVERSITIES,
          variables: queryVariables,
        });
        cache.writeQuery({
          query: GET_UNIVERSITIES,
          variables: queryVariables,
          data: {
            universities: universities.filter((u) => u.id !== uni.id),
          },
        });
      },
    }).then(() => {
      snack.success('University deleted');
    });
  };

  const onAddDocument = (uni) => {
    setAddDoc({ uni });
  };

  const onEditDocument = (uni, doc) => {
    setAddDoc({
      uni,
      doc,
    });
  };

  const onDocumentUploaded = (newDoc) => {
    const { universities } = client.cache.readQuery({
      query: GET_UNIVERSITIES,
      variables: queryVariables,
    });
    const uni = universities.find((u) => u.id === newDoc.university_id);
    uni.documents.push(newDoc);
    client.cache.writeQuery({
      query: GET_UNIVERSITIES,
      variables: queryVariables,
      data: { universities },
    });
  };

  const cc_essay_total =
    creditsResp?.credits.find((i) => i.credit_type === 'cc_essay')?.available ||
    0;
  const cc_essay_done = Math.abs(
    creditsResp?.credits.find((i) => i.credit_type === 'cc_essay')?.debit || 0,
  );
  const hc_essay_total =
    creditsResp?.credits.find((i) => i.credit_type === 'hc_essay')?.available ||
    0;
  const hc_essay_done = Math.abs(
    creditsResp?.credits.find((i) => i.credit_type === 'hc_essay')?.debit || 0,
  );
  const expert_total =
    creditsResp?.credits.find((i) => i.credit_type === 'expert')?.available ||
    0;
  const expert_done = Math.abs(
    creditsResp?.credits.find((i) => i.credit_type === 'expert')?.debit || 0,
  );

  const openInNewTab = (url) => {
    const newWindow = window.open(url, '_blank', 'noopener,noreferrer')
    if (newWindow) newWindow.opener = null
  }

  return (
    <React.Fragment>
      <Helmet title="Supplemental Essays" />
      <Query
        query={GET_UNIVERSITIES}
        fetchPolicy={'cache-and-network'}
        variables={queryVariables}
      >
        {({ data, loading }) => {
          if (loading || !data) return <Loading spinning />;

          const { universities } = data;

          const collegesRemaining =
            creditsResp?.credits.find((i) => i.credit_type === 'colleges')
              ?.available - universities.length;

          return (
            <div>
              <Box marginBottom={3}>
                <Tabs
                  value={pathname}
                  indicatorColor="primary"
                  textColor="primary"
                >
                  <NavTab
                    component={NavLink}
                    to={`${routePrefix}/essays/brainstorm`}
                    value={`${routePrefix}/essays/brainstorm`}
                    label="Brainstorm"
                  />
                  <NavTab
                    component={NavLink}
                    to={`${routePrefix}/essays`}
                    value={`${routePrefix}/essays`}
                    label="Essays"
                  />
                </Tabs>
              </Box>
              <Switch>
                <Route
                  exact
                  path={`${routePrefix}/essays/brainstorm`}
                  component={() => <Brainstorm universities={universities} />}
                />
                <div>
                  {
                    <Box
                      marginBottom={2}
                      display="flex"
                      alignItems="flex-end"
                      flexDirection={{ xs: 'column', sm: 'row' }}
                    >
                      {
                        student.product_type !== 'solo' &&
                        student.product_type !== 'cohort' && (
                          <Box marginBottom={1}>
                            <Typography
                              variant="smallBold"
                              color="textSecondary"
                              style={{ marginRight: 16 }}
                            >
                              CC Revisions Remaining:{' '}
                              {Math.abs(cc_essay_total - cc_essay_done)}
                            </Typography>
                            <Typography
                              variant="smallBold"
                              color="textSecondary"
                              style={{ marginRight: 16 }}
                            >
                              HC Revisions Remaining:{' '}
                              {Math.abs(hc_essay_total - hc_essay_done)}
                            </Typography>
                            <Typography
                              variant="smallBold"
                              color="textSecondary"
                            >
                              Colleges Credit Remaining: {collegesRemaining || 0}
                            </Typography>
                          </Box>
                        )}
                      {student.product_type === 'solo' && (
                        <Box marginBottom={1}>
                          <Typography
                            variant="smallBold"
                            color="textSecondary"
                            style={{ marginRight: 16 }}
                          >
                            Expert Credits Remaining:{' '}
                            {expert_total - expert_done}
                          </Typography>
                        </Box>
                      )}
                      {student.product_type === 'cohort' && (
                        <Box marginBottom={1}>
                          <Typography
                            variant="smallBold"
                            color="textSecondary"
                            style={{ marginRight: 16 }}
                          >
                            Expert Credits Remaining:{' '}
                            {expert_total - expert_done}
                          </Typography>
                        </Box>
                      )}

                      {!readOnly && (
                        <Box marginLeft="20%">
                          <Button
                          
                          style={{ borderRadius: 50, border: '2px solid' }}
                            onClick={() =>openInNewTab('https://www.loom.com/share/e7259e740b6546bca0f697b808c56db8')}
                            size="large"
                            variant="outlined"
                            color="primary"
                          >
                            <HelpOutlineIcon className={classes.leftIcon}/>
                          How this works?
                          </Button>
                        </Box>
                      )}

                      {!readOnly && (
                        <Box marginLeft="auto">
                          <Button
                            onClick={() => onAddUniversity(collegesRemaining)}
                            size="large"
                            variant="contained"
                            color="primary"
                          >
                            Add University
                          </Button>
                        </Box>
                      )}
                    </Box>
                  }
                  <Universities
                    readOnly={readOnly}
                    student={student}
                    universities={universities}
                    onEdit={onEditUniversity}
                    onViewLogs={onViewLogs}
                    onDelete={onDeleteUniversity}
                    onAddDocument={onAddDocument}
                    onUpdateDoc={onEditDocument}
                    hasCCCredit={
                      product_type === 'pro'
                        ? true
                        : cc_essay_total - cc_essay_done > 0
                    }
                    hasHCCredit={
                      product_type === 'pro'
                        ? true
                        : hc_essay_total - hc_essay_done > 0
                    }
                    hasExpertCredit={expert_total - expert_done > 0}
                  />
                  <Footer className="text_center">
                    {!universities.length ? (
                      <Typography gutterBottom variant="h5" color="textPrimary">
                        Supplemental Essays
                      </Typography>
                    ) : null}
                  </Footer>
                </div>
              </Switch>
            </div>
          );
        }}
      </Query>
      {showUniModal && (
        <AddUniversatyModal
          user={student}
          data={showUniModal}
          onSubmit={onAdd}
          onClose={() => setUniModal(false)}
        />
      )}
      {showAddDoc && (
        <AddDocumentModal
          user={student}
          university={showAddDoc.uni}
          doc={showAddDoc.doc}
          onClose={() => setAddDoc()}
          onSubmit={onDocumentUploaded}
        />
      )}
      {showLogs && (
        <ShowLogs
          user={student}
          data={showLogs}
          onClose={() => setShowLogs(false)}
        />
      )}
    </React.Fragment>
  );
};

export default withConfirm(Essays);
